import {
  Component,
  importProvidersFrom,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterLink,
  RouterOutlet,
} from '@angular/router';
import {
  MOTIF_DATE_TIME_FORMATS,
  MotifAvatarModule,
  MotifDropdownModule,
  MotifHeaderModule,
  MotifIconModule,
  MotifThemeModule,
  MotifVerticalNavigationModule,
} from '@ey-xd/ng-motif';
import { CommonModule } from '@angular/common';
import {
  IconoirHalfMoon,
  IconoirHelpCircle,
  IconoirHome,
  IconoirLogOut,
  IconoirSettings,
  IconoirSunLight,
  IconoirUser,
  IconoirTable,
  IconoirShoppingCodeCheck,
  IconoirUserBadgeCheck,
  IconoirChatBubbleQuestion,
  IconoirPageStar,
} from '@ey-xd/motif-icon';
import { HeaderComponent } from './core/components/header/header.component';
import { CoreModule } from './core/core.module';
import { interval, Subject, Subscription, takeUntil } from 'rxjs';
import { AuthenticationService } from './core/service/auth/authentication.service';
import {
  MSAL_GUARD_CONFIG,
  MsalBroadcastService,
  MsalGuardConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionStatus,
} from '@azure/msal-browser';
import { filter, map } from 'rxjs/operators';
import { FooterComponent } from './core/components/footer/footer.component';

import { Idle, DEFAULT_INTERRUPTSOURCES, NgIdleModule } from '@ng-idle/core';
import { Keepalive, NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { CacheService } from 'services/cache.service';
import { Title } from '@angular/platform-browser';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { CancelSameApisInterceptor } from './interceptors/cancel-same-apis.interceptor';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    RouterLink,
    MotifHeaderModule,
    MotifVerticalNavigationModule,
    MotifThemeModule,
    MotifAvatarModule,
    MotifIconModule,
    CommonModule,
    MotifDropdownModule,
    HeaderComponent,
    CoreModule,
    FooterComponent,
  ],
  providers: [
    {
      provide: MOTIF_DATE_TIME_FORMATS,
      useValue: {
        ...MOTIF_DATE_TIME_FORMATS,
        datePickerInput: { year: 'numeric' },
      },
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CancelSameApisInterceptor,
      multi: true,
    },
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy {
  showMenu: boolean = false;
  IconoirSunLight = IconoirSunLight;
  IconoirHalfMoon = IconoirHalfMoon;
  IconoirUser = IconoirUser;
  IconoirLogOut = IconoirLogOut;
  IconoirSettings = IconoirSettings;
  IconoirHelpCircle = IconoirHelpCircle;
  IconoirTable = IconoirTable;
  IconoirShoppingCodeCheck = IconoirShoppingCodeCheck;
  IconoirUserBadgeCheck = IconoirUserBadgeCheck;
  IconoirChatBubbleQuestion = IconoirChatBubbleQuestion;
  private subscription!: Subscription;
  items = [
    {
      label: 'Home',
      icon: IconoirHome,
      exact: true,
      routerLink: [''],
    },
    {
      label:
        'Trial Balance Extraction Agent and UK Chart of Account Mapping Agent',
      icon: IconoirTable,
      routerLink: ['/tb-agent'],
      subMenu: [],
    },
    // {
    //   label: 'Question Generation Agent',
    //   icon: IconoirChatBubbleQuestion,
    //   routerLink: ['/questions-agent'],
    //   subMenu: [],
    // },
    {
      label: 'Corporate Income Tax Review Agent',
      icon: IconoirUserBadgeCheck,
      routerLink: ['/agent-five'],
      subMenu: [],
    },
    {
      label: 'iXBRL Agent',
      icon: IconoirPageStar,
      routerLink: ['/ixbrl'],
      subMenu: [],
    },
  ];
  theme: string = 'dark';
  //-----------------------------------
  isIframe = false;
  loginDisplay = false;
  private loggedInSubscription?: Subscription | null;
  private readonly _destroying$ = new Subject<void>();

  idleState = 'Not started.';
  timedOut = false;
  lastPing?: Date | null = null;

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    public router: Router,
    protected authenticationService: AuthenticationService,
    private cacheService: CacheService,
    private idle: Idle,
    private keepalive: Keepalive,
    private activatedRoute: ActivatedRoute,
    private titleService: Title
  ) {
    this.setTitleOnRouteChange();
    this.cacheService.clearCacheOnVersionChange();

    // alert()
    // sets an idle timeout of 2h
    idle.setIdle(2 * 3600);
    // sets a timeout period of 5 seconds. 15 min + 5s inactivity -> logged out
    idle.setTimeout(5);
    // sets the default interrupts, in this case, things like clicks, scrolls, touches to the document
    idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    idle.onIdleEnd.subscribe(() => (this.idleState = 'No longer idle.'));
    idle.onTimeout.subscribe(() => {
      this.idleState = 'Timed out!';
      this.timedOut = true;
      localStorage.clear();
      this.msalService.logout();
    });
    idle.onIdleStart.subscribe(() => (this.idleState = "You've gone idle!"));
    idle.onTimeoutWarning.subscribe(
      (countdown: any) =>
        (this.idleState = 'You will time out in ' + countdown + ' seconds!')
    );

    // sets the ping interval to 15 seconds
    keepalive.interval(15);

    keepalive.onPing.subscribe(() => (this.lastPing = new Date()));

    this.reset();
  }

  reset() {
    this.idle.watch();
    this.idleState = 'Started.';
    this.timedOut = false;
  }

  logoutOnClose() {
    localStorage.clear();
    this.authenticationService.logoutImmediately();
  }

  public countdown: number = 10 * 60 * 60 - 60; // 9h 59min timeout

  startCountdown(): void {
    const countdown$ = interval(1000); // Emit value every second
    this.subscription = countdown$.subscribe(() => {
      if (this.countdown > 0) {
        this.countdown--;
      } else {
        this.subscription.unsubscribe();
        this.logoutOnClose();
      }
    });
  }

  ngOnInit(): void {
    this.msalService.handleRedirectObservable().subscribe();
    this.isIframe = window !== window.parent && !window.opener; // Remove this line to use Angular Universal

    this.setLoginDisplay();
    this.startCountdown();

    this.msalService.instance.enableAccountStorageEvents();
    this.msalBroadcastService.inProgress$.subscribe(
      (status: InteractionStatus) => {
        // console.log('MSAL Event: InProgress', status);
        if (status === InteractionStatus.None) {
          this.authenticationService.checkAccount();
        }
      }
    );

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.ACCOUNT_ADDED)
      )
      .subscribe((result: EventMessage) => {
        // console.log('Filtered MSAL Event:' + result.eventType, result);
        if (this.msalService.instance.getAllAccounts().length === 0) {
          console.log('MSAL Event: No accounts detected, redirecting to root');
          window.location.pathname = '/';
        }
      });
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_SUCCESS ||
            msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
        )
      )
      .subscribe((result: EventMessage) => {
        if (result.payload && 'account' in result.payload) {
          const payload = result.payload as AuthenticationResult;
          this.authenticationService.checkAccount(payload.account);
        } else {
          console.error('No account in result payload');
        }
      });
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) => msg.eventType === EventType.ACCOUNT_REMOVED
        )
      )
      .subscribe((result: EventMessage) => {
        // console.log('Filtered MSAL Event:' + result.eventType, result);
        if (this.msalService.instance.getActiveAccount() == null) {
          window.location.pathname = '/login';
        }
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();
      });
    if (this.authenticationService.loggedIn) {
    } else {
      this.loggedInSubscription = this.authenticationService
        .isLoggedIn()
        .subscribe((x) => {
          if (x) {
            this.loggedInSubscription?.unsubscribe();
            this.loggedInSubscription = null;
          } else if (!x) {
            this.router.navigate(['login']);
          }
        });
    }
  }
  setLoginDisplay() {
    this.loginDisplay = this.msalService.instance.getAllAccounts().length > 0;
  }

  checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    let activeAccount = this.msalService.instance.getActiveAccount();

    if (
      !activeAccount &&
      this.msalService.instance.getAllAccounts().length > 0
    ) {
      let accounts = this.msalService.instance.getAllAccounts();
      this.msalService.instance.setActiveAccount(accounts[0]);
    }
  }

  private setTitleOnRouteChange(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => {
          let route = this.activatedRoute;
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route.snapshot.data['title'] || 'CIT Agent';
        })
      )
      .subscribe((title: string) => {
        this.titleService.setTitle(title);
      });
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  toggleMenu(): void {
    this.showMenu = !this.showMenu;
  }

  toggleTheme(): void {
    this.theme = this.theme === 'dark' ? 'light' : 'dark';
  }
}
